
import React, { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useNavigate } from "react-router-dom";
import useUser from "../hooks/useUser";

// IMAGEN DE FONDO
import fondoLogin from "../imagenes/SILOS.jpeg";

// ==========================================
// CONFIGURACIÓN DEL SERVIDOR
// ==========================================

const SERVIDOR = "centroformularioscixsa.com";

const PUERTO = ":8080"; // Producción
// const PUERTO = ":9090"; // QA

const API_URL = `https://${SERVIDOR}${PUERTO}`;

// ==========================================
// MENSAJES DEL SISTEMA
// ==========================================

const mensajes = {
  empty:
    "Completa el usuario, la contraseña y el área para continuar.",

  invalid:
    "No fue posible validar la información del usuario.",

  denied:
    "Usuario, contraseña o área incorrectos, o acceso no autorizado.",

  areaMismatch:
    "El área autenticada no coincide con el área seleccionada.",

  network:
    "Error de conexión. Verifica tu red y vuelve a intentarlo.",

  server:
    "Error del servidor. Contacta al área de Aplicaciones.",

  areas:
    "No fue posible consultar las áreas disponibles.",

  context:
    "No fue posible inicializar la sesión del usuario.",
};

// ==========================================
// CONSULTAR API
// ==========================================

async function postJSON(ruta, parametros = {}, signal) {
  const response = await fetch(`${API_URL}${ruta}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(parametros),
    signal,
  });

  if (
    response.status === 204 ||
    response.status === 401 ||
    response.status === 403
  ) {
    throw new Error("denied");
  }

  if (!response.ok) {
    throw new Error(
      response.status >= 500 ? "server" : "invalid"
    );
  }

  const datos = await response.json().catch(() => null);

  if (
    !datos ||
    datos.error ||
    datos.success === false
  ) {
    throw new Error("invalid");
  }

  return datos;
}

// ==========================================
// COMPONENTE LOGIN
// ==========================================

export default function Login() {
  const navigate = useNavigate();
  const user = useUser();

  // DATOS DEL FORMULARIO
  const [codigoUsuario, setCodigoUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [mostrarPassword, setMostrarPassword] =
    useState(false);

  // ÁREAS
  const [idArea, setIdArea] = useState("");
  const [areas, setAreas] = useState([]);

  // ESTADOS
  const [cargandoAreas, setCargandoAreas] = useState(true);
  const [cargando, setCargando] = useState(false);

  const [error, setError] = useState("");
  const [intentoAreas, setIntentoAreas] = useState(0);

  // ==========================================
  // CONSULTAR TODAS LAS ÁREAS
  // ==========================================

  useEffect(() => {
    const controller = new AbortController();

    const consultarAreas = async () => {
      setCargandoAreas(true);
      setError("");

      try {
        const datos = await postJSON(
          "/area/area",
          {},
          controller.signal
        );

        if (controller.signal.aborted) return;

        const lista = Array.isArray(datos.resultado)
          ? datos.resultado
          : [];

        // IMPORTANTE:
        // SE CONSERVAN TODAS LAS ÁREAS.
        // NO SE APLICA NINGÚN FILTRO.

        setAreas(lista);

        if (lista.length === 0) {
          setError("areas");
        }

      } catch (err) {
        if (controller.signal.aborted) return;

        console.error("Error consultando áreas:", err);

        setAreas([]);
        setError("areas");

      } finally {
        if (!controller.signal.aborted) {
          setCargandoAreas(false);
        }
      }
    };

    consultarAreas();

    return () => controller.abort();

  }, [intentoAreas]);

  // ==========================================
  // INICIAR SESIÓN
  // ==========================================

  const ingresar = async (evento) => {
    evento.preventDefault();

    if (cargando) return;

    if (
      !codigoUsuario.trim() ||
      !password ||
      idArea === ""
    ) {
      setError("empty");
      return;
    }

    setCargando(true);
    setError("");

    const parametros = {
      codigo_usuario: codigoUsuario.trim(),
      password: password,
      id_area: Number(idArea),
    };

    try {
      // CONSULTAR SERVICIO DE LOGIN

      const token = await postJSON(
        "/usuario/login",
        parametros
      );

      // VALIDAR RESULTADO

      const datosUsuario = Array.isArray(token.resultado)
        ? token.resultado[0]
        : null;

      if (!datosUsuario) {
        throw new Error("invalid");
      }

      // NORMALIZAR ID DE ÁREA

      const areaAutenticada = Number(
        datosUsuario.id_area
      );

      if (!Number.isInteger(areaAutenticada)) {
        throw new Error("invalid");
      }

      // VALIDAR ÁREA SELECCIONADA

      if (areaAutenticada !== Number(idArea)) {
        throw new Error("areaMismatch");
      }

      if (!user) {
        throw new Error("context");
      }

      // ======================================
      // GUARDAR DATOS DEL USUARIO
      // ======================================

      user.codigo_usuario =
        parametros.codigo_usuario;

      user.nombre_completo =
        datosUsuario.nombre_completo;

      user.id_area = areaAutenticada;

      user.estado_usuario =
        datosUsuario.estado_usuario;

      user.id_usuario =
        datosUsuario.id_usuario;

      user.ip = SERVIDOR;
      user.puerto = PUERTO;

      // NO SE GUARDA LA CONTRASEÑA

      // GUARDAR TOKEN SI EL BACKEND
      // DEVUELVE UNO

      if (
        typeof token.token === "string" &&
        token.token.length > 0
      ) {
        sessionStorage.setItem(
          "token",
          token.token
        );
      } else {
        sessionStorage.removeItem("token");
      }

      setPassword("");

      // ======================================
      // NAVEGACIÓN SEGÚN ÁREA
      // ======================================

      // ÁREA 1: FORMULARIOS
      // ÁREA 7: MANTENIMIENTO DE RESPUESTAS
      // DEMÁS ÁREAS: MENÚ GENERAL

      const destino = [1, 7].includes(areaAutenticada)
        ? "/menu-categorias"
        : "/menu-general";

      navigate(destino, {
        replace: true,
      });

    } catch (err) {
      console.error("Error de login:", err);

      setError(
        mensajes[err.message]
          ? err.message
          : "network"
      );

    } finally {
      setCargando(false);
    }
  };

  // ==========================================
  // INTERFAZ PRINCIPAL
  // ==========================================

  return (
    <Box
      sx={{
        minHeight: "100vh",

        display: "flex",
        alignItems: "center",

        py: {
          xs: 3,
          md: 6,
        },

        px: {
          xs: 2,
          md: 4,
        },

        background:
          "linear-gradient(135deg, #edf5fb 0%, #e2eef9 55%, #f4f8fc 100%)",
      }}
    >
      <Container maxWidth="lg" disableGutters>

        {/* CONTENEDOR PRINCIPAL */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1.08fr) minmax(0, 0.92fr)",
            },

            minHeight: {
              md: 615,
            },

            overflow: "hidden",

            bgcolor: "#ffffff",

            borderRadius: {
              xs: 3,
              md: 5,
            },

            boxShadow:
              "0 22px 70px rgba(18,47,80,.16)",

            border:
              "1px solid rgba(14,60,113,.07)",
          }}
        >

          {/* ==================================
              PANEL IZQUIERDO
              IMAGEN DE LOS SILOS
          ================================== */}

          <Box
            sx={{
              position: "relative",

              display: "flex",

              alignItems: "flex-end",

              minHeight: {
                xs: 310,
                sm: 360,
                md: "auto",
              },

              overflow: "hidden",

              backgroundImage: `
                linear-gradient(
                  180deg,
                  rgba(7,35,72,.12) 0%,
                  rgba(5,36,75,.88) 100%
                ),
                url(${fondoLogin})
              `,

              backgroundSize: "cover",

              backgroundPosition: "center",
            }}
          >

            {/* TÍTULO INSTITUCIONAL */}

            <Box
              sx={{
                position: "relative",

                p: {
                  xs: 3,
                  md: 5,
                },

                color: "#ffffff",
              }}
            >

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,

                  lineHeight: 1.14,

                  fontSize: {
                    xs: 29,
                    md: 43,
                  },

                  maxWidth: 430,
                }}
              >
                Centro de Formularios
              </Typography>

              <Typography
                sx={{
                  mt: 2,

                  opacity: 0.92,

                  maxWidth: 440,

                  fontSize: {
                    xs: 14,
                    md: 16,
                  },

                  lineHeight: 1.7,
                }}
              >
                Plataforma de gestión y administración
                de formularios operativos.
              </Typography>

              {/* LÍNEA DECORATIVA */}

              <Box
                sx={{
                  width: 75,
                  height: 4,

                  bgcolor: "#42a5f5",

                  borderRadius: 3,

                  mt: 3,
                }}
              />

            </Box>
          </Box>

          {/* ==================================
              PANEL DERECHO
              FORMULARIO DE LOGIN
          ================================== */}

          <Box
            component="form"
            onSubmit={ingresar}
            noValidate
            sx={{
              display: "flex",

              flexDirection: "column",

              justifyContent: "center",

              px: {
                xs: 3,
                sm: 5,
                md: 6,
              },

              py: {
                xs: 4,
                md: 6,
              },
            }}
          >

            {/* ICONO SUPERIOR */}

            <Box
              sx={{
                width: 52,
                height: 52,

                borderRadius: 2.5,

                bgcolor: "#e8f2ff",

                color: "#1460bb",

                display: "grid",

                placeItems: "center",

                mb: 2,
              }}
            >
              <ShieldOutlinedIcon
                sx={{
                  fontSize: 30,
                }}
              />
            </Box>

            {/* BIENVENIDA */}

            <Typography
              variant="h4"
              sx={{
                color: "#123967",

                fontWeight: 800,

                fontSize: {
                  xs: 28,
                  md: 33,
                },
              }}
            >
              Bienvenido
            </Typography>

            <Typography
              sx={{
                color: "#64768b",

                mt: 1,

                mb: 3.5,

                fontSize: 14,
              }}
            >
              Ingresa tus credenciales para continuar.
            </Typography>

            {/* ==================================
                CÓDIGO DE USUARIO
            ================================== */}

            <TextField
              fullWidth
              required

              label="Código de usuario"

              placeholder="Ingresa tu código"

              value={codigoUsuario}

              disabled={cargando}

              autoComplete="username"

              onChange={(e) => {
                setCodigoUsuario(e.target.value);
                setError("");
              }}

              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon
                      color="action"
                    />
                  </InputAdornment>
                ),
              }}

              sx={{
                mb: 2.2,
              }}
            />

            {/* ==================================
                CONTRASEÑA
            ================================== */}

            <TextField
              fullWidth
              required

              label="Contraseña"

              placeholder="Ingresa tu contraseña"

              type={
                mostrarPassword
                  ? "text"
                  : "password"
              }

              value={password}

              disabled={cargando}

              autoComplete="current-password"

              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}

              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon
                      color="action"
                    />
                  </InputAdornment>
                ),

                endAdornment: (
                  <InputAdornment position="end">

                    <IconButton
                      edge="end"

                      disabled={cargando}

                      onClick={() =>
                        setMostrarPassword(
                          (valor) => !valor
                        )
                      }

                      aria-label={
                        mostrarPassword
                          ? "Ocultar contraseña"
                          : "Mostrar contraseña"
                      }
                    >
                      {mostrarPassword
                        ? <VisibilityOffIcon />
                        : <VisibilityIcon />}
                    </IconButton>

                  </InputAdornment>
                ),
              }}

              sx={{
                mb: 2.2,
              }}
            />

            {/* ==================================
                ÁREAS DE TRABAJO
            ================================== */}

            <TextField
              select

              fullWidth

              required

              label="Área de trabajo"

              value={idArea}

              disabled={
                cargando ||
                cargandoAreas ||
                areas.length === 0
              }

              onChange={(e) => {
                setIdArea(e.target.value);
                setError("");
              }}

              helperText={
                cargandoAreas
                  ? "Consultando áreas disponibles..."
                  : "Selecciona tu área de trabajo"
              }

              sx={{
                mb: 2.5,
              }}
            >

              {/* MOSTRAR TODAS LAS ÁREAS */}

              {areas.map((area) => (
                <MenuItem
                  key={area.id_area}
                  value={String(area.id_area)}
                >
                  {area.desc_area}
                </MenuItem>
              ))}

            </TextField>

            {/* ==================================
                MENSAJES DE ERROR
            ================================== */}

            {error && (
              <Alert
                severity={
                  error === "empty"
                    ? "warning"
                    : "error"
                }

                sx={{
                  mb: 2,
                  borderRadius: 2,
                }}
              >
                {mensajes[error] || mensajes.server}

                {error === "areas" && (
                  <Button
                    size="small"

                    startIcon={<RefreshIcon />}

                    onClick={() => {
                      setError("");

                      setIntentoAreas(
                        (valor) => valor + 1
                      );
                    }}

                    sx={{
                      ml: 1,
                    }}
                  >
                    Reintentar
                  </Button>
                )}

              </Alert>
            )}

            {/* ==================================
                BOTÓN INGRESAR
            ================================== */}

            <Button
              type="submit"

              fullWidth

              variant="contained"

              disabled={
                cargando ||
                cargandoAreas ||
                !areas.length
              }

              endIcon={
                !cargando
                  ? <ArrowForwardRoundedIcon />
                  : null
              }

              sx={{
                py: 1.6,

                textTransform: "none",

                fontSize: 16,

                fontWeight: 700,

                borderRadius: 2,

                bgcolor: "#1769c2",

                boxShadow:
                  "0 8px 18px rgba(23,105,194,.22)",

                "&:hover": {
                  bgcolor: "#1157a5",
                },
              }}
            >
              {cargando ? (
                <>
                  <CircularProgress
                    size={20}

                    color="inherit"

                    sx={{
                      mr: 1.5,
                    }}
                  />

                  Validando acceso...
                </>
              ) : (
                "Ingresar al sistema"
              )}
            </Button>

            {/* ==================================
                PIE DEL FORMULARIO
            ================================== */}

            <Typography
              sx={{
                color: "#8290a0",

                fontSize: 12,

                textAlign: "center",

                mt: 3,
              }}
            >
              Acceso exclusivo para usuarios autorizados.
            </Typography>

          </Box>
        </Box>
      </Container>
    </Box>
  );
}
